import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    LockOutlined,
    BorderAll,
    Storage,
    Chair,
    CheckCircleOutline,
    LightbulbOutlined,
    Image as ImageIcon,
    HomeOutlined,
    ChevronRight
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェアでのプライベート空間の意義' },
    { id: 'partition', label: 'パーテーション活用で叶える自分だけのスペース' },
    { id: 'storage', label: '収納アイデアでスッキリ＆快適な個室化' },
    { id: 'interior', label: 'DIYインテリア実例と空間コーディネート' },
    { id: 'expert-advice', label: '成功のポイントと専門家からのアドバイス' },
    { id: 'conclusion', label: 'まとめ：自分らしい空間で快適なルームシェアを' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.1rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left', p: 0 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアでのプライベート空間の意義
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、経済的なメリットだけでなく、日常に多様な刺激や人間関係をもたらしてくれます。しかし、共同生活ゆえに「自分だけの空間がほしい」「リラックスできるパーソナルスペースが足りない」と感じる方も少なくありません。<br />
                    そこで今回は、<b>DIYで叶える快適なプライベート空間の作り方</b>を徹底解説！パーテーションや収納の活用、ちょっとしたインテリアの工夫で、あなたの「自分らしいスペース」を実現しましょう。<br />
                    <Box sx={{ mt: 3, mb: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                    <img
                                        src="/images/blog/roomshare-dining_roomshare-private-space-diy.jpg"
                                        alt="明るい共有リビングで、ナチュラルな木目のテーブルを囲み、複数のルームメイトが和やかに食事や会話を楽しんでいるシーン。カジュアルで温かみのある雰囲気が伝わる"
                                        style={{ width: '100%', height: 'auto', display: 'block' }}
                                    />
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5fafd', borderRadius: 2, height: '100%' }}>
                                    <Typography variant="body2" sx={{ color: '#1565c0' }}>
                                        <LockOutlined fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        プライバシーと快適な暮らし、どちらも叶えるヒントを紹介します！
                                    </Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Chip label="DIY" color="primary" variant="outlined" sx={{ mr: 1 }} />
                                    <Chip label="収納" color="primary" variant="outlined" sx={{ mr: 1 }} />
                                    <Chip label="インテリア" color="primary" variant="outlined" />
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                    この記事では、<b>誰でも手軽にできるDIYアイデア</b>と、<b>心地よい空間作りのコツ</b>を専門的な視点で解説します。ルームシェア生活をもっと快適に、自分らしくアップデートしてみませんか？
                </Typography>
            </Box>

            {/* パーテーション活用 */}
            <Box id="partition" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    パーテーション活用で叶える自分だけのスペース
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアで最も手軽にプライベート空間を確保する方法が、<b>パーテーション（間仕切り）の活用</b>です。市販品からDIYまで、空間や用途に応じた選択肢が豊富にあります。
                </Typography>
                <Grid container spacing={3} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <img
                                src="/images/blog/partition-diy_roomshare-private-space-diy.jpg"
                                alt="白い布と木材フレームでDIYされたパーテーションが、明るいワンルーム空間をゆるやかに仕切っている様子。シンプルかつおしゃれな雰囲気"
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <List>
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemIcon>
                                    <BorderAll color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="subtitle1" fontWeight="bold">おすすめパーテーション例</Typography>}
                                    secondary={
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}><b>・置き型パネル：</b>移動やレイアウト変更が簡単。賃貸でも安心。</Typography>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}><b>・カーテンタイプ：</b>コストを抑えて柔軟に空間を仕切れる。</Typography>
                                            <Typography variant="body2"><b>・DIYパーテーション：</b>木材や布、すのこを活用して自分仕様に。</Typography>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        </List>
                    </Grid>
                </Grid>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    <LightbulbOutlined color="secondary" fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    パーテーションの色や素材を部屋の雰囲気に合わせると、圧迫感が減り、インテリアの一部としても楽しめます。
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                    <CheckCircleOutline color="success" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    DIYパーテーションの作り方ポイント
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon><ChevronRight /></ListItemIcon>
                        <ListItemText
                            primary="100均すのこ＋布で簡単間仕切り"
                            secondary="すのこを組み合わせ、布を掛けるだけで軽やかな仕切りに。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ChevronRight /></ListItemIcon>
                        <ListItemText
                            primary="木枠＋カーテンリングで可動式"
                            secondary="DIY初心者も挑戦しやすく、必要なときだけ仕切れるのが魅力。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ChevronRight /></ListItemIcon>
                        <ListItemText
                            primary="収納兼用パーテーション"
                            secondary="本棚やラックを間仕切り代わりに活用すれば、スペースの有効活用にも。"
                        />
                    </ListItem>
                </List>
                <Box sx={{ mt: 2 }}>
                    <Chip label="パーテーションDIY" color="secondary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="賃貸OK" color="success" variant="outlined" />
                </Box>
            </Box>

            {/* 収納アイデア */}
            <Box id="storage" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    収納アイデアでスッキリ＆快適な個室化
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでは収納スペースが限られがち。<b>収納の工夫</b>で見た目も気分もスッキリさせ、プライベート感もアップできます。
                </Typography>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <img
                                src="/images/blog/storage-idea_roomshare-private-space-diy.jpg"
                                alt="ベッド下に収納ボックスを並べ、壁面にはシンプルな棚やフックが配置されたミニマルな個室。明るい色調で統一され、コンパクトながらも機能的な印象"
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <Storage color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="subtitle1" fontWeight="bold">収納アイデア集</Typography>}
                                    secondary={
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}><b>・ベッド下：</b>衣類や季節物を隠して収納。</Typography>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}><b>・壁面収納：</b>棚やフックを活用してデッドスペースを有効利用。</Typography>
                                            <Typography variant="body2"><b>・カラーボックス：</b>仕切り兼収納として間仕切りにも活躍。</Typography>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        </List>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                    <CheckCircleOutline color="success" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    収納DIYのポイント
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon><ChevronRight /></ListItemIcon>
                        <ListItemText
                            primary="突っ張り棒＋布カバーで隠す収納"
                            secondary="見せたくない物はさっと隠せて、空間が広く見える効果も。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ChevronRight /></ListItemIcon>
                        <ListItemText
                            primary="壁掛け収納で床面すっきり"
                            secondary="シェルフやフックで壁を活用。掃除もラクに。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ChevronRight /></ListItemIcon>
                        <ListItemText
                            primary="多機能家具を選ぶ"
                            secondary="ベッド下収納や折りたたみテーブルなど、省スペース＆収納力を両立。"
                        />
                    </ListItem>
                </List>
                <Box sx={{ mt: 2 }}>
                    <Chip label="収納DIY" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="省スペース" color="success" variant="outlined" />
                </Box>
            </Box>

            {/* インテリア実例 */}
            <Box id="interior" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    DIYインテリア実例と空間コーディネート
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    プライベート空間作りの仕上げは、やっぱり<b>インテリア</b>。自分らしい小物や色使い、照明をプラスして、心地よい居場所を演出しましょう。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <img
                                src="/images/blog/interior-example_roomshare-private-space-diy.jpg"
                                alt="ナチュラルウッドのデスクと観葉植物、カラフルなクッションがアクセントになった個室スペース。柔らかな照明が温かな雰囲気を演出している"
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ bgcolor: '#f5f5f5', boxShadow: 0 }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                    <Chair color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    取り入れたいインテリアアイデア
                                </Typography>
                                <List>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon>
                                            <LightbulbOutlined color="warning" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="差し色クッションやラグ"
                                            secondary="小物で色を取り入れて、個性を演出。"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon>
                                            <LightbulbOutlined color="warning" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="観葉植物やアートパネル"
                                            secondary="グリーンやアートで癒やしと彩りを。"
                                        />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemIcon>
                                            <LightbulbOutlined color="warning" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="テーブルランプや間接照明"
                                            secondary="照明を工夫して、柔らかな雰囲気づくり。"
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ mb: 2 }}>
                    <HomeOutlined color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    インテリアは「自分だけのくつろぎ」を意識して。季節ごとに小物をチェンジするのもおすすめです。
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Chip label="インテリア" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="快適空間" color="success" variant="outlined" />
                </Box>
            </Box>

            {/* 専門家アドバイス */}
            <Box id="expert-advice" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    成功のポイントと専門的アドバイス
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    実際にルームシェアでプライベート空間を作る際は、機能性だけでなく安全性やルームメイトとのコミュニケーションにも気を配ることが成功の秘訣です。
                </Typography>
                <Card sx={{ bgcolor: '#f9fbe7', borderRadius: 2, boxShadow: 0, mb: 2 }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                            <CheckCircleOutline color="success" sx={{ mr: 1, verticalAlign: 'middle' }} />
                            専門家が伝える「快適な個室化」のコツ
                        </Typography>
                        <List>
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemIcon>
                                    <LightbulbOutlined color="info" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="安全・転倒防止を意識"
                                    secondary="パーテーションや収納は転倒防止グッズや壁固定で安全性を高めましょう。"
                                />
                            </ListItem>
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemIcon>
                                    <LightbulbOutlined color="info" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="生活動線を意識して配置"
                                    secondary="動線を妨げないレイアウトで、共用部と個室のバランスを意識。"
                                />
                            </ListItem>
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemIcon>
                                    <LightbulbOutlined color="info" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="ルームメイトとの相談・合意"
                                    secondary="DIYや配置変更の際は、事前にルームメイトと話し合うことがトラブル防止に。"
                                />
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
                <Typography variant="body2">
                    <CheckCircleOutline color="success" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    <b>ポイント</b>：自分だけでなく、全員が快適に過ごせるよう思いやりを忘れずに！
                </Typography>
            </Box>

            {/* まとめ */}
            <Box id="conclusion" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ：自分らしい空間で快適なルームシェアを
                </Typography>
                <Typography variant="body1">
                    ルームシェアでのプライベート空間作りは、<b>ちょっとしたDIYや収納の工夫</b>で大きく変わります。パーテーションで空間を区切り、収納で整え、インテリアで自分らしさをプラス。それぞれの工夫が、毎日の暮らしをもっと心地よくしてくれます。<br /><br />
                    ぜひあなたも、今回紹介したアイデアを取り入れて、<b>自分らしく豊かなルームシェアライフ</b>を楽しんでください！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-private-space-diy',
    slug: 'roomshare-private-space-diy',
    title: 'ルームシェアでのプライベート空間の作り方・DIYアイデア',
    excerpt: 'DIYで個室を快適に\nパーテーション活用\n収納の工夫\nインテリア例',
    coverImage: '/images/blog/cover-roomshare-private-space-diy_roomshare-private-space-diy.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木目調の家具と観葉植物が置かれ、温かく快適な雰囲気が感じられる',
    publishedAt: '2024-06-12T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', 'ライフハック', 'インテリア'],
    tags: ['DIY', '収納', 'パーテーション', 'プライベート空間', 'インテリア'],
    author: {
        name: '高橋 遼',
        avatar: '/images/avatars/ryo-takahashi.jpg',
        bio: 'ルームシェア・賃貸インテリアの実践的アドバイザー。全国のシェアハウスや賃貸物件でのDIY・収納術に詳しく、快適な共同生活のための空間づくりを提案しています。',
        profilePicture: '/images/avatars/ryo-takahashi.jpg'
    },
    viewCount: 1080,
    contentComponent: PostContent
};

export default post;